import type { ContextTag } from '~/models/contextTags'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import {
  validateWeightEntry,
  type WeightEntryInput,
  type WeightEntryRecord,
} from '~/models/weightEntry'
import { type CalendarDate, toCalendarDate, toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { PhaseId } from '~/types/ids'
import { toKilograms } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

export async function getWeightEntry(
  db: SolaDatabase,
  date: CalendarDate,
): Promise<Result<WeightEntryRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.weightEntries, unwrapDate(date)))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Create or overwrite the entry for a calendar date.
 *
 * The store is keyed by date, so one entry per day is structural. Re-entering
 * overwrites and records correction metadata rather than creating a second row
 * (D-035). `createdAt` is preserved so a correction does not look like a new
 * entry.
 */
export async function putWeightEntry(
  db: SolaDatabase,
  input: WeightEntryInput,
  now: Date,
  phaseId?: PhaseId,
): Promise<Result<WeightEntryRecord, StorageError>> {
  const issues = validateWeightEntry(input)
  if (issues.length > 0) {
    return err(
      storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues),
    )
  }

  const date = toCalendarDate(input.date)
  const weightKg = toKilograms(input.weightKg)
  if (date === undefined || weightKg === undefined) {
    return err(storageError('invalidRecord', 'Weight entry failed unit or date construction.'))
  }

  try {
    const existing = await db.get(STORE.weightEntries, unwrapDate(date))
    const timestamp = toIsoTimestamp(now)
    const isCorrection = existing !== undefined && existing.weightKg !== weightKg

    const record: WeightEntryRecord = {
      date,
      weightKg,
      contextTags: (input.contextTags ?? []) as readonly ContextTag[],
      ...(input.note === undefined ? {} : { note: input.note }),
      ...(phaseId === undefined ? {} : { phaseId }),
      ...(isCorrection ? { correctedFromKg: existing.weightKg, correctedAt: timestamp } : {}),
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
      schemaVersion: DB_SCHEMA_VERSION,
    }

    await db.put(STORE.weightEntries, record)
    notifyWritten(STORE.weightEntries)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Entries within an inclusive calendar-date range.
 *
 * A single bounded key-range scan — the reason the store uses the date as its
 * natural key. Lexical ordering is correct for zero-padded `YYYY-MM-DD`.
 */
export async function listWeightEntriesInRange(
  db: SolaDatabase,
  from: CalendarDate,
  to: CalendarDate,
): Promise<Result<readonly WeightEntryRecord[], StorageError>> {
  try {
    const range = IDBKeyRange.bound(unwrapDate(from), unwrapDate(to))
    return ok(await db.getAll(STORE.weightEntries, range))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function deleteWeightEntry(
  db: SolaDatabase,
  date: CalendarDate,
): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE.weightEntries, unwrapDate(date))
    notifyWritten(STORE.weightEntries)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function countWeightEntries(db: SolaDatabase): Promise<Result<number, StorageError>> {
  try {
    return ok(await db.count(STORE.weightEntries))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
